// src/components/engine/LossAttributionBlock.jsx — v2.3
// Fix v2.3: ENGINE_ROOT_CAUSE_MAP takes priority over keyword classification.
// When attribution.root_cause is present (engine v2.0+), it's used directly.
// Keyword matching from reasons[] remains as fallback for compatibility.

// ── Engine v2.0 direct map ────────────────────────────────────────────────────
// Priority source: attribution.root_cause from /api/analyze response
const ENGINE_ROOT_CAUSE_MAP = {
  CR_DROPPED:        { type: "conversion",  label: "Conversion rate drop",    color: "#e85555" },
  SPEND_SPIKE:       { type: "spend",       label: "Spend increased sharply", color: "#e85555" },
  SPEND_DROP:        { type: "spend",       label: "Spend dropped sharply",   color: "#f5a623" },
  ATTRIBUTION_DELAY: { type: "attribution", label: "Attribution delayed",     color: "#f5a623" },
  REVENUE_DROP:      { type: "conversion",  label: "Revenue fell sharply",    color: "#e85555" },
  ROI_DEGRADED:      { type: "conversion",  label: "Gradual performance decay", color: "#f5a623" },
  MULTI_SIGNAL:      { type: "unknown",     label: "Multiple signals",        color: "#94a3b8" },
};

// ── Keyword fallback (pre-v2.0 compatibility) ─────────────────────────────────
const ANOMALY_RULES = [
  { type: "attribution", color: "#f5a623", label: "Attribution delayed",
    keywords: ["attribution", "delayed", "stale", "data freshness", "webhook error", "freshness"] },
  { type: "conversion",  color: "#e85555", label: "Conversion rate drop",
    keywords: ["conversion", "CR ", "cr drop", "degraded", "baseline by"] },
  { type: "spend",       color: "#e85555", label: "Spend increase",
    keywords: ["velocity", "spend velocity", "traffic cost", "overspend", "budget", "pace"] },
  { type: "data",        color: "#94a3b8", label: "Insufficient data",
    keywords: ["data point", "insufficient", "no baseline", "campaign age", "only "] },
];

const ROOT_CAUSE_LABELS = {
  attribution: "Attribution data arrived late — results may have appeared worse than they were",
  conversion:  "Conversions dropped significantly compared to your campaign's normal performance",
  spend:       "Ad spend accelerated faster than expected relative to results",
  data:        "Not enough data yet to determine the exact cause",
  unknown:     "Multiple performance signals deteriorated at the same time",
};

/**
 * Derives anomaly type from engine root_cause (v2.0) or reasons array (fallback).
 * @param {string[]} reasons
 * @param {string}   risk_status
 * @param {string}   root_cause  — from attribution.root_cause (engine v2.0)
 * @returns {{ type: string, label: string, color: string, matched_signals: string[] }}
 */
export function deriveAnomalyType(reasons = [], risk_status = "medium", root_cause = "") {
  // Priority: engine v2.0 root_cause
  if (root_cause && ENGINE_ROOT_CAUSE_MAP[root_cause]) {
    return { ...ENGINE_ROOT_CAUSE_MAP[root_cause], matched_signals: [] };
  }

  // Fallback: keyword matching from reasons[]
  const text = reasons.join(" ").toLowerCase();

  const scores = ANOMALY_RULES.map(rule => ({
    ...rule,
    score: rule.keywords.filter(k => text.includes(k.toLowerCase())).length,
  }));

  const best = scores.sort((a, b) => b.score - a.score)[0];

  if (!best || best.score === 0) {
    if (risk_status === "critical") {
      return { type: "spend", label: "Spend increase", color: "#e85555", matched_signals: [] };
    }
    return { type: "unknown", label: "Multiple signals", color: "#94a3b8", matched_signals: [] };
  }

  const matched_signals = reasons.filter(r =>
    best.keywords.some(k => r.toLowerCase().includes(k.toLowerCase()))
  );

  return { type: best.type, label: best.label, color: best.color, matched_signals };
}

// ── Grouped signals display ───────────────────────────────────────────────────
function GroupedSignals({ signals, color }) {
  if (!signals?.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 6 }}>
      {signals.map((s, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          <span style={{ color, flexShrink: 0, marginTop: 1, fontSize: 10 }}>◈</span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: 10,
                         color: "var(--text-secondary)", lineHeight: 1.5 }}>
            {s}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── LossAttributionBlock ──────────────────────────────────────────────────────
/**
 * @param {{
 *   reasons?: string[],
 *   risk_status?: string,
 *   root_cause?: string,
 *   compact?: boolean,
 * }} props
 */
export default function LossAttributionBlock({
  reasons = [],
  risk_status = "medium",
  root_cause = "",
  compact = false,
}) {
  if (!reasons.length && !risk_status && !root_cause) return null;

  const anomaly   = deriveAnomalyType(reasons, risk_status, root_cause);
  const root_text = ROOT_CAUSE_LABELS[anomaly.type] ?? ROOT_CAUSE_LABELS.unknown;

  if (compact) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{
          padding: "2px 7px", borderRadius: "var(--radius-sm)",
          background: `${anomaly.color}18`, border: `1px solid ${anomaly.color}30`,
          fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700,
          color: anomaly.color, textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          {anomaly.label}
        </span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--text-muted)" }}>
          Root cause
        </span>
      </div>
    );
  }

  return (
    <div style={{
      padding: "12px 16px", borderRadius: "var(--radius)",
      background: `${anomaly.color}09`, border: `1px solid ${anomaly.color}22`,
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span style={{
            fontFamily: "var(--font-mono)", fontSize: 9, letterSpacing: "0.14em",
            textTransform: "uppercase", color: "var(--text-muted)",
          }}>
            Root Cause
          </span>
          <span style={{
            padding: "2px 8px", borderRadius: "var(--radius-sm)",
            background: `${anomaly.color}18`, border: `1px solid ${anomaly.color}30`,
            fontFamily: "var(--font-mono)", fontSize: 9, fontWeight: 700,
            color: anomaly.color, textTransform: "uppercase",
          }}>
            {anomaly.label}
          </span>
        </div>
      </div>

      {/* Root cause description */}
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.55, marginBottom: 4 }}>
        {root_text}
      </div>

      {/* Matched signals */}
      <GroupedSignals signals={anomaly.matched_signals} color={anomaly.color} />
    </div>
  );
}
